import {
  cashPayProductEnvelopeSchema,
  cashPayProductListEnvelopeSchema,
  cashPayPurchaseEnvelopeSchema,
  cashPayPurchaseListEnvelopeSchema,
  consentDocumentEnvelopeSchema,
  consentDocumentListEnvelopeSchema,
  createCashPayProductRequestSchema,
  createCashPayPurchaseRequestSchema,
  createConsentDocumentRequestSchema,
  createInjectableAdministrationRequestSchema,
  createPatientPhotoRequestSchema,
  createRetailSaleRequestSchema,
  injectableAdministrationEnvelopeSchema,
  injectableAdministrationListEnvelopeSchema,
  patientPhotoEnvelopeSchema,
  patientPhotoListEnvelopeSchema,
  redeemCashPayUnitsRequestSchema,
  retailSaleEnvelopeSchema,
  retailSaleListEnvelopeSchema,
  type CashPayProductResponse,
  type CashPayPurchaseResponse,
  type ConsentDocumentResponse,
  type CreateCashPayProductRequest,
  type CreateCashPayPurchaseRequest,
  type CreateConsentDocumentRequest,
  type CreateInjectableAdministrationRequest,
  type CreatePatientPhotoRequest,
  type CreateRetailSaleRequest,
  type InjectableAdministrationResponse,
  type PatientPhotoResponse,
  type RedeemCashPayUnitsRequest,
  type RetailSaleResponse,
} from '@emr/shared';

import { apiRequest } from './client.ts';

/**
 * The aesthetics/med-spa specialty-module API surface (`BACKLOG.md` M9), validated against
 * `@emr/shared`'s own request/response schemas — the exact same schemas `server/src/aesthetics/
 * routes.ts` validates request bodies with, matching `api/dermatology.ts`'s own stated reasoning for
 * why this cannot silently drift from what the server actually accepts and returns.
 */

export type {
  CashPayProductResponse,
  CashPayPurchaseResponse,
  ConsentDocumentResponse,
  CreateCashPayProductRequest,
  CreateCashPayPurchaseRequest,
  CreateConsentDocumentRequest,
  CreateInjectableAdministrationRequest,
  CreatePatientPhotoRequest,
  CreateRetailSaleRequest,
  InjectableAdministrationResponse,
  PatientPhotoResponse,
  RedeemCashPayUnitsRequest,
  RetailSaleResponse,
};

function aes(patientId: string, suffix = ''): string {
  return `/api/patients/${patientId}/aesthetics${suffix}`;
}

// --- Consent documents ----------------------------------------------------------------------------

export async function fetchConsentDocuments(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly ConsentDocumentResponse[]> {
  const response = await apiRequest(
    aes(patientId, '/consents'),
    consentDocumentListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.consents;
}

export async function createConsentDocument(
  patientId: string,
  input: CreateConsentDocumentRequest,
): Promise<ConsentDocumentResponse> {
  createConsentDocumentRequestSchema.parse(input);
  const response = await apiRequest(aes(patientId, '/consents'), consentDocumentEnvelopeSchema, {
    method: 'POST',
    body: input,
  });
  return response.consent;
}

export async function revokeConsentDocument(
  patientId: string,
  consentId: string,
): Promise<ConsentDocumentResponse> {
  const response = await apiRequest(
    aes(patientId, `/consents/${consentId}/revoke`),
    consentDocumentEnvelopeSchema,
    { method: 'POST', body: {} },
  );
  return response.consent;
}

// --- Photo documentation ---------------------------------------------------------------------------

export async function fetchPatientPhotos(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly PatientPhotoResponse[]> {
  const response = await apiRequest(
    aes(patientId, '/photos'),
    patientPhotoListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.photos;
}

export async function createPatientPhoto(
  patientId: string,
  input: CreatePatientPhotoRequest,
): Promise<PatientPhotoResponse> {
  createPatientPhotoRequestSchema.parse(input);
  const response = await apiRequest(aes(patientId, '/photos'), patientPhotoEnvelopeSchema, {
    method: 'POST',
    body: input,
  });
  return response.photo;
}

// --- Injectable charting ---------------------------------------------------------------------------

export async function fetchInjectableAdministrations(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly InjectableAdministrationResponse[]> {
  const response = await apiRequest(
    aes(patientId, '/injectables'),
    injectableAdministrationListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.administrations;
}

export async function createInjectableAdministration(
  patientId: string,
  input: CreateInjectableAdministrationRequest,
): Promise<InjectableAdministrationResponse> {
  createInjectableAdministrationRequestSchema.parse(input);
  const response = await apiRequest(
    aes(patientId, '/injectables'),
    injectableAdministrationEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.administration;
}

// --- Cash-pay billing: product catalog (org-wide) ---------------------------------------------------

export async function fetchCashPayProducts(
  signal?: AbortSignal,
): Promise<readonly CashPayProductResponse[]> {
  const response = await apiRequest(
    '/api/aesthetics/cash-pay-products',
    cashPayProductListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.products;
}

export async function createCashPayProduct(
  input: CreateCashPayProductRequest,
): Promise<CashPayProductResponse> {
  createCashPayProductRequestSchema.parse(input);
  const response = await apiRequest(
    '/api/aesthetics/cash-pay-products',
    cashPayProductEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.product;
}

// --- Cash-pay billing: patient purchases -------------------------------------------------------

export async function fetchCashPayPurchases(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly CashPayPurchaseResponse[]> {
  const response = await apiRequest(
    aes(patientId, '/cash-pay-purchases'),
    cashPayPurchaseListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.purchases;
}

export async function createCashPayPurchase(
  patientId: string,
  input: CreateCashPayPurchaseRequest,
): Promise<CashPayPurchaseResponse> {
  createCashPayPurchaseRequestSchema.parse(input);
  const response = await apiRequest(
    aes(patientId, '/cash-pay-purchases'),
    cashPayPurchaseEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.purchase;
}

export async function redeemCashPayUnits(
  patientId: string,
  purchaseId: string,
  input: RedeemCashPayUnitsRequest,
): Promise<CashPayPurchaseResponse> {
  redeemCashPayUnitsRequestSchema.parse(input);
  const response = await apiRequest(
    aes(patientId, `/cash-pay-purchases/${purchaseId}/redeem`),
    cashPayPurchaseEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.purchase;
}

// --- Retail POS -------------------------------------------------------------------------------------

export async function fetchRetailSales(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly RetailSaleResponse[]> {
  const response = await apiRequest(
    aes(patientId, '/retail-sales'),
    retailSaleListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.sales;
}

export async function createRetailSale(
  patientId: string,
  input: CreateRetailSaleRequest,
): Promise<RetailSaleResponse> {
  createRetailSaleRequestSchema.parse(input);
  const response = await apiRequest(aes(patientId, '/retail-sales'), retailSaleEnvelopeSchema, {
    method: 'POST',
    body: input,
  });
  return response.sale;
}
